import { FormEventHandler, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { Toast } from "@sscs/ui";
import { apiErrorToast } from "Utils";
import * as yup from "Utils/yup";
import { useInitiatePasswordResetMutation } from "../services/passwordReset.api";

type RequestResetFormData = {
  email: string;
};

type UseRequestResetForm = {
  errors: FieldErrors<RequestResetFormData>;
  isSubmitSuccessful: boolean;
  isValid: boolean;
  methods: UseFormReturn<RequestResetFormData>;
  onSubmit: FormEventHandler<HTMLFormElement>;
};

const schema: yup.SchemaOf<RequestResetFormData> = yup.object({
  email: yup
    .string()
    .email("Enter a valid email address")
    .required("Email address is required"),
});

const useRequestResetForm = (
  recaptchaResponse: string | null | undefined,
): UseRequestResetForm => {
  const defaultValues = useMemo(
    () => ({
      email: "",
    }),
    [],
  );

  const [requestReset] = useInitiatePasswordResetMutation();

  const methods = useForm<RequestResetFormData>({
    resolver: yupResolver(schema),
    mode: "onTouched",
    defaultValues,
  });

  const {
    formState: { errors, isSubmitSuccessful, isValid, isDirty },
    handleSubmit,
  } = methods;

  const onSubmit: SubmitHandler<RequestResetFormData> = async (data) => {
    try {
      if (recaptchaResponse) {
        await requestReset({
          body: {
            email: data.email,
            "g-recaptcha-response": recaptchaResponse,
          },
        }).unwrap();

        Toast.show({
          title: "Password reset email requested",
          type: "success",
        });
      }
    } catch (error) {
      apiErrorToast(error, "Unable to process password reset request");
    }
  };

  return {
    errors,
    isSubmitSuccessful,
    isValid: isValid && isDirty,
    methods,
    onSubmit: handleSubmit(onSubmit),
  };
};

export default useRequestResetForm;
